import { ChangeEvent, useState } from "react";
import { ToggleInput } from "@datagame/ui";
import { useMutation } from "@apollo/client";
import { useTranslation } from "react-i18next";

import { ColorInput } from "../../../../../../../shared/ui/ColorInput";
import {
  Game,
  LibraryImage,
  LibraryImageKind,
  WordopolisConfiguration,
  WordopolisConfigurationUpdateInput,
} from "../../../../../../../../gql/graphql";
import { ImageInput, Section } from "../../../Common";
import { WORDOPOLIS_CONFIGURATION_UPDATE } from "../../../../../../../../graphql/mutations/GameMutation";
import { TooltipWithIcon } from "../../../../../../../shared/ui/TooltipWithIcon";

import styles from "./BoardBackground.module.scss";

export interface BoardBackgroundProps {
  game: Game & { configuration: { __typename?: "WordopolisConfiguration" } };
}

export const BoardBackground = ({ game }: BoardBackgroundProps) => {
  const { t: translate } = useTranslation("project");
  const t = (key: string) => translate(`customizeVisuals.form.boardBackground.${key}`);
  const configuration = game.configuration as WordopolisConfiguration;
  const [isUsingImage, setIsUsingImage] = useState(Boolean(configuration.boardBackgroundImage));
  const [backgroundColor, setBackgroundColor] = useState(configuration.boardBackgroundColor);
  const [boardFontColor, setBoardFontColor] = useState(configuration.boardColor);

  const [updateGameConfiguration] = useMutation(WORDOPOLIS_CONFIGURATION_UPDATE);

  const onUpdate = async (change: WordopolisConfigurationUpdateInput) => {
    await updateGameConfiguration({
      variables: {
        wordopolisConfigurationId: configuration.id,
        data: change,
      },
    });
  };

  const handleBoardBackgroundImageUpdate = async (image?: LibraryImage) => {
    onUpdate({ boardBackgroundImageId: image?.id ?? null });
  };

  const handleIsUsingImageUpdate = async ({ target: { checked } }: ChangeEvent<HTMLInputElement>) => {
    setIsUsingImage(checked);
    if (!checked) {
      handleBoardBackgroundImageUpdate();
    }
  };

  return (
    <>
      <Section title={t("title")} className={styles.section} tooltip={<TooltipWithIcon content={t("titleTooltip")} />}>
        {isUsingImage ? (
          <ImageInput
            image={configuration.boardBackgroundImage}
            kind={LibraryImageKind.Background}
            onUpload={handleBoardBackgroundImageUpdate}
            onRemove={handleBoardBackgroundImageUpdate}
            showRemoveButton={true}
          />
        ) : (
          <ColorInput
            value={backgroundColor}
            onChange={(color) => setBackgroundColor(color)}
            onBlur={() => onUpdate({ boardBackgroundColor: backgroundColor })}
          />
        )}

        <ToggleInput
          className={styles.toggle}
          name="wordopolisBackgroundImageToggle"
          label={t("toggleLabel")}
          value={isUsingImage}
          onChange={handleIsUsingImageUpdate}
          tooltip={<TooltipWithIcon content={t("toggleTooltip")} />}
        />
      </Section>
      <Section
        title={t("fontSectionTitle")}
        className={styles.section}
        tooltip={<TooltipWithIcon content={t("fontSectionTooltip")} />}
      >
        <ColorInput
          value={boardFontColor}
          onChange={(color) => setBoardFontColor(color)}
          onBlur={() => onUpdate({ boardColor: boardFontColor })}
        />
      </Section>
    </>
  );
};
